import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { theme } from '../styles/theme';
import { SharedLayoutUltimate } from '../components/layout/SharedLayoutUltimate';


const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #1a2332 0%, #2c3e50 50%, #1a2332 100%);
  padding: 120px 20px 80px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, rgba(255, 215, 0, 0.05) 0%, transparent 70%);
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 200%;
    height: 100%;
    top: 0;
    left: -50%;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(255, 215, 0, 0.03) 45%, 
      rgba(255, 215, 0, 0.06) 50%, 
      rgba(255, 215, 0, 0.03) 55%, 
      transparent 100%
    );
    animation: ${shimmer} 15s infinite linear;
    pointer-events: none;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 100px 15px 60px;
  }
`;

const TestimonialsSection = styled.div`
  padding: 100px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 50%, #fff9e6 100%);
  position: relative;
  overflow: hidden;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 60px 15px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.xl};
  position: relative;
  z-index: 1;

  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 0 ${theme.spacing.md};
  }
`;

const PageTitle = styled.h1`
  text-align: center;
  margin-bottom: 20px;
  font-size: clamp(42px, 6vw, 72px);
  color: white;
  font-weight: 900;
  letter-spacing: -2px;
  line-height: 1.1;
  
  .gold {
    color: #ffd700;
    text-shadow: 0 0 30px rgba(255, 215, 0, 0.5);
    animation: ${pulse} 3s ease-in-out infinite;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: clamp(32px, 8vw, 48px);
    letter-spacing: -1px;
  }
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 20px;
  font-size: clamp(36px, 5vw, 56px);
  color: #1a2332;
  font-weight: 900;
  letter-spacing: -1.5px;
  
  .gold {
    color: #ffd700;
    text-shadow: 0 2px 10px rgba(255, 215, 0, 0.3);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: clamp(30px, 6vw, 40px);
  }
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: clamp(20px, 3vw, 28px);
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 60px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  font-weight: 400;
  line-height: 1.5;
  opacity: 0.95;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: clamp(18px, 4vw, 22px);
    margin-bottom: 40px;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: clamp(20px, 2.5vw, 26px);
  color: #666;
  margin-bottom: 60px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: clamp(18px, 3vw, 22px);
    margin-bottom: 40px;
  }
`;

const TrustBadges = styled.div`
  display: flex;
  justify-content: center;
  gap: 50px;
  margin-bottom: 60px;
  flex-wrap: wrap;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 30px;
    margin-bottom: 40px;
  }
`;

const Badge = styled.div<{ delay?: string }>`
  text-align: center;
  animation: ${float} 4s ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};
  
  .number {
    font-size: clamp(36px, 5vw, 48px);
    font-weight: 900;
    color: #ffd700;
    text-shadow: 0 2px 10px rgba(255, 215, 0, 0.3);
    margin-bottom: 5px;
  }
  
  .label {
    font-size: clamp(12px, 1.5vw, 14px);
    text-transform: uppercase;
    color: #666;
    letter-spacing: 1px;
    font-weight: 600;
  }
`;

const TestimonialsScroller = styled.div`
  position: relative;
  overflow: hidden;
  margin-bottom: 40px;
`;

const AutoScrollControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 40px;
  flex-wrap: wrap;
`;

const ControlButton = styled.button<{ active?: boolean }>`
  background: ${props => props.active ? 'linear-gradient(135deg, #ffd700, #ffa500)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.active ? '#1a2332' : '#ffd700'};
  border: 2px solid ${props => props.active ? 'transparent' : 'rgba(255, 215, 0, 0.3)'};
  padding: 12px 24px;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 700;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: ${props => props.active ? '0 8px 20px rgba(255, 215, 0, 0.3)' : '0 4px 10px rgba(0, 0, 0, 0.1)'};
  
  &:hover {
    transform: translateY(-2px);
    background: linear-gradient(135deg, #ffd700, #ffa500);
    color: #1a2332;
    border-color: transparent;
    box-shadow: 0 10px 25px rgba(255, 215, 0, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const TestimonialsTrack = styled.div`
  display: flex;
  gap: 30px;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  padding: ${theme.spacing.md} 0;
  scroll-snap-type: x mandatory;
  width: 100%;
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${theme.colors.neutral.lightGrey};
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.primary.blue};
    border-radius: 10px;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    gap: ${theme.spacing.md};
  }
`;

const TestimonialCard = styled.div`
  background: white;
  border-radius: 25px;
  overflow: hidden;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  flex: 0 0 400px;
  scroll-snap-align: center;
  border: 3px solid transparent;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 25px;
    padding: 3px;
    background: linear-gradient(135deg, #ffd700, #ffa500);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.3s;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15);
    
    &::before {
      opacity: 0.5;
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex: 0 0 300px;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  background: linear-gradient(135deg, #1a2332, #2c3e50);
  
  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const TestimonialContent = styled.div`
  padding: 25px;
  background: linear-gradient(135deg, #ffffff, #f8f9fa);
`;


const ClientName = styled.p`
  font-size: 22px;
  font-weight: 800;
  color: #1a2332;
  margin-bottom: 5px;
`;

const ClientTitle = styled.p`
  font-size: 16px;
  color: #666;
`;

const ClientSince = styled.span`
  color: #ffd700;
  font-weight: 600;
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #1a2332 0%, #2c3e50 50%, #1a2332 100%);
  padding: 80px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 50% 50%, rgba(16, 163, 127, 0.1) 0%, transparent 70%);
    pointer-events: none;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 60px 20px;
  }
`;

const CTATitle = styled.h2`
  font-size: clamp(36px, 5vw, 52px);
  color: white;
  margin-bottom: 20px;
  font-weight: 900;
  letter-spacing: -1px;
  line-height: 1.1;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: clamp(28px, 6vw, 36px);
  }
`;

const CTASubtitle = styled.p`
  font-size: clamp(18px, 2.5vw, 24px);
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 40px;
  line-height: 1.5;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: clamp(16px, 3vw, 20px);
    margin-bottom: 30px;
  }
`;

const CTAButton = styled.button`
  display: block;
  margin: 40px auto 0;
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  color: #1a1a1a;
  padding: 22px 50px;
  border: none;
  border-radius: 50px;
  font-size: clamp(18px, 2.5vw, 22px);
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 15px 40px rgba(255, 215, 0, 0.4);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.6);
    transform: translate(-50%, -50%);
    transition: width 0.5s, height 0.5s;
  }
  
  &:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 20px 50px rgba(255, 215, 0, 0.5);
    
    &::before {
      width: 400px;
      height: 400px;
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 18px 35px;
    font-size: clamp(16px, 3vw, 18px);
  }
`;


export const Testimonials: React.FC = () => {
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const testimonials = [
    {
      id: 1,
      name: "Reuben",
      title: "Business Owner",
      year: "2020",
      videoUrl: "https://player.vimeo.com/video/846822421",
      quote: "Finally understood how annuities could work for my financial future. The team was patient and thorough in their explanations."
    },
    {
      id: 2,
      name: "Robert",
      title: "Retired Executive",
      year: "2019",
      videoUrl: "https://player.vimeo.com/video/846822473",
      quote: "The personalized consultation helped me make the right choice for my family's long-term security."
    },
    {
      id: 3,
      name: "Connie",
      title: "Financial Professional",
      year: "2017",
      videoUrl: "https://player.vimeo.com/video/846819836",
      quote: "I recommend this service to all my clients looking for annuity solutions. Their expertise is unmatched."
    },
    {
      id: 4,
      name: "Jeff",
      title: "Retired Teacher",
      year: "2021",
      videoUrl: "https://player.vimeo.com/video/846819893",
      quote: "Straightforward advice that helped secure my family's financial stability for years to come."
    },
    {
      id: 5,
      name: "Clement",
      title: "Retired Engineer",
      year: "2018",
      videoUrl: "https://player.vimeo.com/video/846822448",
      quote: "The annuity guidance I received was invaluable for my retirement planning. They helped me understand complex products in simple terms."
    }
  ];

  const scrollToTestimonial = (index: number) => {
    if (scrollRef.current) {
      const cardWidth = 430; // Card width (400px) + gap (30px)
      const scrollLeft = index * cardWidth;
      console.log(`Scrolling to testimonial ${index}, scrollLeft: ${scrollLeft}`);
      scrollRef.current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });
      setCurrentIndex(index);
    }
  };

  const startAutoScroll = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    
    intervalRef.current = setInterval(() => {
      setCurrentIndex(prev => {
        const nextIndex = prev >= testimonials.length - 1 ? 0 : prev + 1;
        scrollToTestimonial(nextIndex);
        return nextIndex;
      });
    }, 4000); // Change testimonial every 4 seconds
  };

  const stopAutoScroll = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (isAutoScrolling) {
      startAutoScroll();
    } else {
      stopAutoScroll();
    }

    return () => stopAutoScroll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAutoScrolling, testimonials.length]);

  const toggleAutoScroll = () => {
    setIsAutoScrolling(!isAutoScrolling);
  };

  const handleManualScroll = (index: number) => {
    setIsAutoScrolling(false);
    scrollToTestimonial(index);
  };

  return (
    <SharedLayoutUltimate>
      {/* Hero Section */}
      <HeroSection>
        <Container>
          <PageTitle>
            <span className="gold">Happy Clients</span> Tell The Story
          </PageTitle>
          <PageSubtitle>
            40 years. Blemish free record. We show the product we use in our own portfolio.
          </PageSubtitle>
        </Container>
      </HeroSection>
      
      {/* Main Testimonials Section */}
      <TestimonialsSection>
        <Container>
          <SectionTitle>
            Real <span className="gold">Success Stories</span>
          </SectionTitle>
          <SectionSubtitle>
            Hear from our 10,000+ satisfied clients
          </SectionSubtitle>
          
          <TrustBadges>
            <Badge delay="0s">
              <div className="number">40</div>
              <div className="label">Years Experience</div>
            </Badge>
            <Badge delay="0.2s">
              <div className="number">10K+</div>
              <div className="label">Clients Served</div>
            </Badge>
            <Badge delay="0.4s">
              <div className="number">Zero</div>
              <div className="label">Complaints</div>
            </Badge>
            <Badge delay="0.6s">
              <div className="number">100%</div>
              <div className="label">Free Service</div>
            </Badge>
          </TrustBadges>
          
          <AutoScrollControls>
            <ControlButton 
              active={isAutoScrolling} 
              onClick={toggleAutoScroll}
            >
              {isAutoScrolling ? '⏸️ Pause Auto-Scroll' : '▶️ Start Auto-Scroll'}
            </ControlButton>
            {testimonials.map((_, index) => (
              <ControlButton
                key={index}
                active={currentIndex === index}
                onClick={() => handleManualScroll(index)}
              >
                {index + 1}
              </ControlButton>
            ))}
          </AutoScrollControls>
          
          <TestimonialsScroller>
            <TestimonialsTrack ref={scrollRef}>
              {testimonials.map((testimonial, index) => (
                <TestimonialCard key={testimonial.id}>
                <VideoContainer>
                  <iframe
                    src={testimonial.videoUrl}
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title={`Testimonial from ${testimonial.name}`}
                  />
                </VideoContainer>
                <TestimonialContent>
                  <ClientName>{testimonial.name}</ClientName>
                  {/* Keeping details commented as in original */}
                  {/* <ClientTitle>
                    {testimonial.title} · <ClientSince>Client since {testimonial.year}</ClientSince>
                  </ClientTitle> */}
                </TestimonialContent>
              </TestimonialCard>
              ))}
            </TestimonialsTrack>
          </TestimonialsScroller>
          
          <div style={{ textAlign: 'center', marginTop: '20px', color: '#999' }}>
            ← Swipe to see more testimonials →
          </div>
        </Container>
      </TestimonialsSection>
      
      {/* CTA Section */}
      <CTASection>
        <Container>
          <CTATitle>Ready to Get Your Annuity Analyzed?</CTATitle>
          <CTASubtitle>
            Join 10,000+ satisfied clients who trust our 40 years of expertise
          </CTASubtitle>
          <CTAButton onClick={() => window.location.href = '/contact'}>
            Get Your Free Analysis
          </CTAButton>
          <CTAButton 
            onClick={() => window.location.href = '/'} 
            style={{ 
              marginTop: '20px', 
              background: 'transparent',
              border: '3px solid #ffd700',
              color: '#ffd700'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 215, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            Back to Home
          </CTAButton>
        </Container>
      </CTASection>
    </SharedLayoutUltimate>
  );
};